/**
 * Next Gen Reporting API client (Phase 3).
 *
 * Every endpoint is flag-gated server-side: a 404 here means the org is not
 * flagged into NGR, not that the resource is missing — callers should treat
 * 404 as "surface off" and render nothing.
 */
import api from '../lib/api';

export interface NgrCubeMember {
  name: string;
  title: string;
  shortTitle?: string;
  type: string;
}

export interface NgrCubeMeta {
  name: string;
  title: string;
  description?: string;
  measures: NgrCubeMember[];
  dimensions: NgrCubeMember[];
}

/** Cube query JSON — the subset the wizard produces. */
export interface NgrQuery {
  measures?: string[];
  dimensions?: string[];
  limit?: number;
}

export interface NgrReportConfig {
  query: NgrQuery;
  layout: 'table';
  charts: unknown[];
  branding: Record<string, unknown>;
  scope: { workspaceIds: string[] | 'all' };
  availability: null | { workspaceIds: string[] } | { orgWide: true };
}

export interface NgrReportDefinition {
  id: number;
  name: string;
  description: string | null;
  config: NgrReportConfig;
  created_at: string;
  updated_at?: string;
  owner_name?: string | null;
  last_run_at?: string | null;
}

export async function fetchNgrMeta(organizationId: string): Promise<NgrCubeMeta[]> {
  const res = await api.get(`/ngr/organizations/${organizationId}/meta`);
  return res.data.data.cubes;
}

export async function fetchNgrReports(organizationId: string): Promise<NgrReportDefinition[]> {
  const res = await api.get(`/ngr/organizations/${organizationId}/reports`);
  return res.data.data;
}

export async function createNgrReport(
  organizationId: string,
  payload: { name: string; description?: string; config: NgrReportConfig }
): Promise<NgrReportDefinition> {
  const res = await api.post(`/ngr/organizations/${organizationId}/reports`, payload);
  return res.data.data;
}

export async function updateNgrReport(
  organizationId: string,
  reportId: number,
  payload: { name: string; description?: string; config: NgrReportConfig }
): Promise<NgrReportDefinition> {
  const res = await api.put(`/ngr/organizations/${organizationId}/reports/${reportId}`, payload);
  return res.data.data;
}

export async function deleteNgrReport(organizationId: string, reportId: number): Promise<void> {
  await api.delete(`/ngr/organizations/${organizationId}/reports/${reportId}`);
}

/** A Cube-backed standard report (backend/src/services/ngrPresets.js). */
export interface NgrPreset {
  key: string;
  name: string;
  description: string;
  view: string;
  entity: string;
  layouts: string[];
  defaultLayout: string;
  query: NgrQuery;
  columns: { member: string; label: string; customField?: boolean; fieldType?: string }[];
}

/**
 * Standard reports that run through Cube. A shelf card whose key is absent
 * here still runs the legacy renderer — reports migrate one at a time.
 */
export async function fetchNgrPresets(organizationId: string): Promise<NgrPreset[]> {
  const res = await api.get(`/ngr/organizations/${organizationId}/presets`);
  return res.data.data;
}

export async function executeNgrPreset(
  organizationId: string,
  presetKey: string
): Promise<{
  rows: Record<string, unknown>[];
  executionTimeMs: number;
  columns: NgrPreset['columns'];
}> {
  const res = await api.post(`/ngr/organizations/${organizationId}/reports/execute`, { presetKey });
  return {
    rows: res.data.data,
    executionTimeMs: res.data.meta.executionTimeMs,
    columns: res.data.meta.columns || [],
  };
}

export async function executeNgrQuery(
  organizationId: string,
  query: NgrQuery
): Promise<{ rows: Record<string, unknown>[]; executionTimeMs: number }> {
  const res = await api.post(`/ngr/organizations/${organizationId}/reports/execute`, { query });
  return { rows: res.data.data, executionTimeMs: res.data.meta.executionTimeMs };
}
